import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Shell from '../Shell';
import { api } from '../api';
import { useToast } from '../ToastContext';
import Structuring from '../sections/Structuring';
import Dispatch from '../sections/Dispatch';
import Responses from '../sections/Responses';
import Comparison from '../sections/Comparison';
import Decide from '../sections/Decide';
import Audit from '../sections/Audit';
import CustomerQuote from '../sections/CustomerQuote';
import Logistics from '../sections/Logistics';

export default function RFQDetail() {
  const { id, tab = 'overview' } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState(null);
  // selectedLineIds: set by Lines tab, consumed by Dispatch tab
  const [selectedLineIds, setSelectedLineIds] = useState([]);

  // Bumped on every reload. Tabs that keep their own server state include this
  // in their effect deps so a commit made on ANOTHER tab cannot leave them stale
  // (R6: the Decide tab's revisions list showed "Rev 2" only after a manual page
  // reload, because the revision was committed from the Compare tab).
  const [refreshKey, setRefreshKey] = useState(0);

  async function reload() {
    const r = await api.getRFQ(id);
    setData(r);
    setRefreshKey((k) => k + 1);
    if (r.rfq.customerId) {
      const list = await api.listCustomers();
      setCustomer(list.items.find((c) => String(c._id) === String(r.rfq.customerId)));
    }
  }
  useEffect(() => { reload().catch((e) => toast.push(e.message, { variant: 'error' })); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <Shell title="Loading…"><div className="empty">Loading…</div></Shell>;

  const tabs = [
    ['overview', 'Overview'],
    ['structure', 'Lines'],
    ['dispatch', 'Dispatch'],
    ['responses', 'Responses'],
    ['compare', 'Compare'],
    ['decide', 'Decide'],
    ['audit', 'Audit'],
    ['customer-quote', 'Customer Quote'],
    ...(data.rfq.logisticsRequired ? [['logistics', 'Logistics']] : []),
  ];

  return (
    <Shell
      title={`${data.rfq.rfqId} — ${customer?.name || ''} · ${data.rfq.title}`}
      subtitle={`${data.lines.length} lines · status ${data.rfq.status}`}
      breadcrumb={<><Link to="/inbox">Inbox</Link> / <span className="mono">{data.rfq.rfqId}</span> / {tab}</>}
      actions={<ExportMenu rfqId={id} />}
    >
      <div className="tabs" style={{ margin: '0 -24px', padding: '0 24px' }}>
        {tabs.map(([key, label]) => (
          <button key={key} className={'tab ' + (tab === key ? 'active' : '')} onClick={() => nav(`/rfq/${id}/${key}`)}>{label}</button>
        ))}
      </div>
      <div style={{ marginTop: 20 }}>
        {tab === 'overview' && <Overview data={data} customer={customer} />}
        {tab === 'structure' && <Structuring data={data} reload={reload} selectedLineIds={selectedLineIds} onSelectionChange={(ids) => { setSelectedLineIds(ids); nav(`/rfq/${id}/dispatch`); }} />}
        {tab === 'dispatch' && <Dispatch data={data} reload={reload} preselectedLineIds={selectedLineIds} onClearPreselect={() => setSelectedLineIds([])} />}
        {tab === 'responses' && <Responses data={data} reload={reload} />}
        {/* `reload` refreshes the shell's own RFQ document. Without it the header
            kept reading "status PartialQuotes" after every line had been awarded
            (R6): committing updates rfq.status server-side, but Comparison only
            refetched its own comparison payload and the shell never re-read the
            RFQ. */}
        {tab === 'compare' && <Comparison rfqId={id} reload={reload} />}
        {tab === 'decide' && <Decide rfqId={id} refreshKey={refreshKey} />}
        {tab === 'audit' && <Audit rfqId={id} />}
        {tab === 'customer-quote' && <CustomerQuote rfqId={id} rfq={data.rfq} rfqLines={data.lines} />}
        {tab === 'logistics' && <Logistics rfqId={id} />}
      </div>
    </Shell>
  );
}

function ExportMenu({ rfqId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="btn btn-secondary btn-sm" onClick={() => setOpen((o) => !o)}>
        ⤓ Export
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '110%',
            background: 'var(--surface-0, #fff)',
            border: '1px solid var(--border, #d0d5dd)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            minWidth: 140,
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <a
            href={api.rfqExportUrl(rfqId, 'json')}
            download
            className="btn btn-ghost"
            style={{ display: 'block', borderRadius: 0, textAlign: 'left', padding: '10px 16px' }}
            onClick={() => setOpen(false)}
          >
            JSON
          </a>
          <a
            href={api.rfqExportUrl(rfqId, 'xlsx')}
            download
            className="btn btn-ghost"
            style={{ display: 'block', borderRadius: 0, textAlign: 'left', padding: '10px 16px' }}
            onClick={() => setOpen(false)}
          >
            Excel (.xlsx)
          </a>
        </div>
      )}
    </div>
  );
}

function Overview({ data, customer }) {
  return (
    <div className="row" style={{ alignItems: 'flex-start' }}>
      <div className="card">
        <h2 className="card-title">Summary</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><b>RFQ ID</b><div className="mono">{data.rfq.rfqId}</div></div>
          <div><b>Status</b><div>{data.rfq.status}</div></div>
          <div><b>Customer</b><div>{customer?.name}</div></div>
          <div><b>Priority</b><div>{data.rfq.priority}</div></div>
          <div><b>Currency</b><div>{data.rfq.currencyPref}</div></div>
          <div><b>Quote mode</b><div>{data.rfq.quoteMode}</div></div>
          <div><b>Lines</b><div>{data.lines.length}</div></div>
          <div><b>Received</b><div>{new Date(data.rfq.receivedAt || data.rfq.createdAt).toLocaleString()}</div></div>
        </div>
        {data.rfq.notes && <div style={{ marginTop: 16 }}><b>Notes</b><div>{data.rfq.notes}</div></div>}
      </div>
      {data.rfq.originalEmailBody && (
        <div className="card section-bg" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
          <h2 className="card-title">Original email</h2>
          {data.rfq.originalEmailBody}
        </div>
      )}
    </div>
  );
}
